import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { BRAND } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function makeSession() {
  const k = "ragi-chat-session";
  try {
    const existing = localStorage.getItem(k);
    if (existing) return existing;
    const s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(k, s);
    return s;
  } catch {
    return `s_${Date.now()}`;
  }
}

const GREETING = "Hey, how can I help you? Tell me what you're looking for and I'll point you in the right direction.";
const QUICK = [
  "I need a new brand identity",
  "Redesign our website",
  "Packaging for a product",
  "Book a 60-min meeting",
];

export default function EyesMascot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(makeSession);
  const listRef = useRef(null);
  const leftEyeRef = useRef(null);
  const rightEyeRef = useRef(null);
  const [pupilL, setPupilL] = useState({ x: 0, y: 0 });
  const [pupilR, setPupilR] = useState({ x: 0, y: 0 });
  const [blink, setBlink] = useState(false);

  // Cursor-tracking pupils
  useEffect(() => {
    const compute = (ref, setP) => (e) => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const max = 5; // max pupil travel in px
      const nx = (dx / (dist || 1)) * Math.min(max, dist / 20);
      const ny = (dy / (dist || 1)) * Math.min(max, dist / 20);
      setP({ x: nx, y: ny });
    };
    const onMoveL = compute(leftEyeRef, setPupilL);
    const onMoveR = compute(rightEyeRef, setPupilR);
    const onMove = (e) => {
      onMoveL(e);
      onMoveR(e);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Periodic blink
  useEffect(() => {
    const t = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 140);
    }, 4200);
    return () => clearInterval(t);
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: value }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: value,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I couldn't reach the AI just now. WhatsApp us on +91 96193 17339 or email hello@ragi.design and we'll respond within one business day.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Mascot, fixed bottom-left */}
      <button
        data-testid="eyes-mascot"
        aria-label={open ? "Close chat" : "Open chat and ask us anything"}
        onClick={() => setOpen((v) => !v)}
        className="group pointer-events-auto fixed bottom-4 left-4 z-40 flex h-16 w-24 items-center justify-center rounded-full border border-ivory/10 bg-ink-800/90 backdrop-blur transition-all hover:border-gold hover:bg-ink-900 lg:bottom-6 lg:left-6 lg:h-20 lg:w-32"
      >
        {/* Left eye */}
        <div
          ref={leftEyeRef}
          className="relative mr-2 flex h-9 w-9 items-center justify-center rounded-full bg-ivory lg:h-11 lg:w-11"
        >
          <motion.div
            animate={{ scaleY: blink ? 0.1 : 1 }}
            transition={{ duration: 0.12 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <motion.span
              style={{ x: pupilL.x, y: pupilL.y }}
              transition={{ type: "spring", stiffness: 150, damping: 15 }}
              className="block h-3 w-3 rounded-full bg-ink-950 lg:h-3.5 lg:w-3.5"
            />
          </motion.div>
        </div>
        {/* Right eye */}
        <div
          ref={rightEyeRef}
          className="relative flex h-9 w-9 items-center justify-center rounded-full bg-ivory lg:h-11 lg:w-11"
        >
          <motion.div
            animate={{ scaleY: blink ? 0.1 : 1 }}
            transition={{ duration: 0.12 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <motion.span
              style={{ x: pupilR.x, y: pupilR.y }}
              transition={{ type: "spring", stiffness: 150, damping: 15 }}
              className="block h-3 w-3 rounded-full bg-ink-950 lg:h-3.5 lg:w-3.5"
            />
          </motion.div>
        </div>

        {/* Pulse dot */}
        {!open ? (
          <span className="absolute -right-1 -top-1 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-gold" />
          </span>
        ) : null}
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open ? (
          <motion.aside
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.76, 0, 0.24, 1] }}
            className="pointer-events-auto fixed bottom-24 left-4 z-50 flex h-[68vh] max-h-[560px] w-[min(94vw,380px)] flex-col overflow-hidden rounded-2xl border border-ivory/10 bg-ink-900/95 shadow-2xl backdrop-blur-xl lg:bottom-32 lg:left-6"
          >
            <header className="flex items-center justify-between border-b border-ivory/[0.06] px-5 py-4">
              <div>
                <div className="font-sans text-[10px] uppercase tracking-widest text-gold">
                  Ragi Concierge
                </div>
                <div className="mt-1 flex items-center gap-2 font-sans text-[11px] text-ivory-muted">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" />
                  Live · replies in seconds
                </div>
              </div>
              <button
                data-testid="chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-ivory/10 text-ivory hover:border-gold hover:text-gold"
              >
                <X size={14} />
              </button>
            </header>

            <div
              ref={listRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-5"
              data-lenis-prevent
            >
              {messages.map((m, i) => (
                <ChatBubble key={i} role={m.role} content={m.content} />
              ))}
              {sending ? <TypingIndicator /> : null}
            </div>

            {messages.length <= 1 ? (
              <div className="flex flex-wrap gap-2 border-t border-ivory/[0.06] px-4 py-3">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="rounded-full border border-ivory/10 px-3 py-1.5 font-sans text-[11px] text-ivory hover:border-gold hover:text-gold"
                  >
                    {q}
                  </button>
                ))}
              </div>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-ivory/[0.06] px-4 py-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message…"
                className="flex-1 bg-transparent font-sans text-sm text-ivory placeholder:text-ivory-dim focus:outline-none"
              />
              <button
                data-testid="chat-send"
                type="submit"
                disabled={sending || !input.trim()}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gold text-ink-950 transition-opacity disabled:opacity-50"
                aria-label="Send"
              >
                <Send size={13} />
              </button>
            </form>

            <div className="flex items-center justify-between border-t border-ivory/[0.06] px-4 py-2.5 font-sans text-[10px] uppercase tracking-widest text-ivory-dim">
              <a
                href={BRAND.whatsappHref}
                target="_blank"
                rel="noreferrer noopener"
                className="hover:text-gold"
              >
                WhatsApp
              </a>
              <a href={`mailto:${BRAND.email}`} className="hover:text-gold">
                {BRAND.email}
              </a>
              <a
                href={BRAND.meetingHref}
                className="inline-flex items-center gap-1 text-gold hover:text-ivory"
              >
                Book <ArrowUpRight size={11} />
              </a>
            </div>
          </motion.aside>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-3 font-sans text-sm leading-relaxed ${
          isUser
            ? "bg-gold text-ink-950 rounded-br-sm"
            : "bg-ink-800 text-ivory rounded-bl-sm border border-ivory/[0.06]"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-1.5 rounded-2xl border border-ivory/[0.06] bg-ink-800 px-4 py-3">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{ y: [0, -3, 0], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.12 }}
            className="block h-1.5 w-1.5 rounded-full bg-gold"
          />
        ))}
      </div>
    </div>
  );
}
